/* External */
import {
  memo, useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Switch,
  Route,
  Redirect,
  useHistory,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';

/* Components */
import { useBootstrap } from '~/hooks/useBootstrap';
import Public from '../routes/Public';
import Private from '../routes/Private';
import ErrorPage from '../routes/ErrorPage';
import MessagePage from '../routes/MessagePage';
import Logout from '../routes/Logout';
import FullScreenMessage, { MessageId } from '../components/FullScreenMessage';

/* Storage */
import { cleanAllEntities, stopAuthenticatedSagas } from '../storage/utils/actions';

/* Services */
import { gaService } from '../services/gaService';

/* Utils */
import { Routes, getPath, getLink } from '../utils/router';
import { useLoading } from '../providers/LoadingProvider';
import { useOnBackPress } from '../utils/keyDownEvents';
import { getMajorVersion, noOp } from '../utils/functions';
import { useIsLoadingUserAccount, useIsUpdatingUserCustomData } from '../providers/UserProvider';
import useAnalitycNavigation from '../hooks/useAnalitycNavigation';
import { useUpdateConnectionStatus, useShowDisconnectedMessage } from '../providers/ConnectionStatusProvider';
import { useDeviceInfo } from '../providers/DeviceInfoProvider';
import platform from '../utils/platform';
import { previousUrlAvailable } from '../utils/history';
import { REPORT_GA } from '../utils/constants';
import { useIsFetchingConfig } from '../providers/ConfigProvider/ConfigProvider';
import { useIsForegroundApp } from '../providers/ForegroundAppProvider';
import { GtmLocalProvider } from '../providers/GtmLocalProvider';
import { MqttProvider } from '../providers/MqttProvider';
import { useGoogleTagsCommon } from '../utils/googleTags';
import { useTermsOfServiceInfo } from '../providers/TermsOfServiceProvider';
import { startUpAnimationTimeToHideSpinner } from '../utils/env';

/* Styles */
import './App.scss';
import '../theme/colors';
import '../theme/fonts';

function App({ hideLoading = noOp, showLoading = noOp, hideStartupLoading = noOp }) {
  const [redirectTo, setRedirectTo] = useState(undefined);
  const { isLoading } = useLoading();
  const history = useHistory();

  const isLoadingUser = useIsLoadingUserAccount();
  const isUpdatingUser = useIsUpdatingUserCustomData();
  const isFetchingConfig = useIsFetchingConfig();
  const isForegroundApp = useIsForegroundApp();
  const { isLoadingShouldAccept } = useTermsOfServiceInfo();
  const dispatch = useDispatch();
  const showDisconnectedMessage = useShowDisconnectedMessage();
  const { sdkVersion } = useDeviceInfo() || {};
  const updateConnectionStatus = useUpdateConnectionStatus();

  useAnalitycNavigation();
  const { isAuthenticated } = useBootstrap();

  const animationClassName = platform.isWebOS && getMajorVersion(sdkVersion) === 3
    ? 'dtv-html5-s-no-transition-and-animation'
    : '';

  const getPathRedirect = useCallback((url) => {
    const match = /^directvgo:(?:\/\/[^/]+)?(\/.*)$/i.exec(url);
    if (match) {
      const [, path] = match;
      return path;
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!isAuthenticated && !isLoadingUser) {
      dispatch(cleanAllEntities());
      dispatch(stopAuthenticatedSagas());
    }
  }, [dispatch, isLoadingUser, isAuthenticated]);

  useEffect(() => {
    if (!isLoading && !isLoadingUser && !isFetchingConfig) {
      hideStartupLoading();
    }
  }, [
    hideStartupLoading,
    isFetchingConfig,
    isLoading,
    isLoadingUser,
  ]);

  useEffect(() => {
    if (
      !isLoading
      && !isLoadingUser
      && !isFetchingConfig
      && !isUpdatingUser
      && !isLoadingShouldAccept
    ) {
      const startupLoaderContainer = document.querySelector('.startup-loader-container');
      if (startupLoaderContainer) {
        hideLoading();
        setTimeout(() => {
          startupLoaderContainer?.remove();
        }, startUpAnimationTimeToHideSpinner || 500);
      }
    }
  }, [
    isLoading,
    isLoadingUser,
    isFetchingConfig,
    isUpdatingUser,
    hideLoading,
    isLoadingShouldAccept,
  ]);

  useEffect(() => {
    if (isLoading
      || isLoadingUser
      || isFetchingConfig
      || isUpdatingUser
      || isLoadingShouldAccept
    ) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [
    hideLoading,
    showLoading,
    isLoading,
    isLoadingUser,
    isFetchingConfig,
    isUpdatingUser,
    isLoadingShouldAccept,
  ]);

  useEffect(() => platform.addRelaunchEventListener((url) => {
    updateConnectionStatus();

    const path = getPathRedirect(url);
    if (path) {
      history.push(path);
    }
  }), [getPathRedirect, history, updateConnectionStatus]);

  useEffect(() => {
    if (isForegroundApp) {
      gaService.sendEvent({
        action: REPORT_GA.GA_EVENT_ACTION_OPEN_APP,
        label: REPORT_GA.GA_EVENT_LABEL_OPEN_APP,
        category: REPORT_GA.GA_EVENT_CATEGORY_OPEN_APP,
      });
    }
  }, [isForegroundApp]);

  const onBackPress = useCallback(() => {
    if (previousUrlAvailable()) {
      history.goBack();
    } else {
      history.push(getLink(Routes.Private.Home));
    }
  }, [history]);

  useEffect(() => {
    async function setRedirectToFromPlatform() {
      const url = await platform.getUrlToOpen();
      const path = getPathRedirect(url);
      if (path) {
        setRedirectTo(path);
      } else {
        setRedirectTo('');
      }
    }

    setRedirectToFromPlatform();
  }, [getPathRedirect]);

  useOnBackPress({ listener: onBackPress, priority: -1 });

  return (
    <GtmLocalProvider commonDimensionsData={useGoogleTagsCommon()}>
      <MqttProvider>
        <div className={animationClassName}>
          <Switch>
            <Route path={getPath(Routes.Logout)} component={Logout} />
            <Route path={getPath(Routes.ErrorPage)} component={ErrorPage} />
            <Route path={getPath(Routes.MessagePage)} component={MessagePage} />
            <Route path={getPath(Routes.Public)} component={Public} />
            <Route path={getPath(Routes.Private)} component={Private} />
            {redirectTo !== undefined && (
              <Switch>
                {!!redirectTo && <Redirect to={redirectTo} />}
                {!isLoadingUser
                  && (isAuthenticated ? (
                    <Redirect to={getLink(Routes.Private)} />
                  ) : (
                    <Redirect to={getLink(Routes.Public)} />
                  ))}
              </Switch>
            )}
          </Switch>
          {showDisconnectedMessage && (
            <FullScreenMessage messageKey={MessageId.LOST_CONNECTION} />
          )}
        </div>
      </MqttProvider>
    </GtmLocalProvider>
  );
}

App.propTypes = {
  hideLoading: PropTypes.func,
  hideStartupLoading: PropTypes.func,
  showLoading: PropTypes.func,
};

export default memo(App);
